import { PlayCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Space, Tag, Typography } from 'antd';
import {
  Alert,
  Button,
  Card,
  Modal,
} from '@insight-health-ai/roseate/components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { SuperAdminContext, SuperAdminScope } from 'src/components';
import { BackIconMobile } from 'src/icons';
import { usePageHeader } from 'src/hooks/misc';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import OrganizationFilter from '../OrganizationFilter';
import { FlowBuilder } from './components/FlowBuilder';
import { DEFAULT_FLOW_BUILDER_VALUE } from './components/FlowBuilder.types';
import { MagicFlowRecipePrefillCard } from './components/MagicFlowRecipePrefillCard';
import { MagicFlowVersionsCard } from './components/MagicFlowVersionsCard';
import { MagicFlowVersionsProvider } from './MagicFlowVersionsContext';
import type {
  FlowRecord,
  FlowRunPriorityRecord,
  FlowStep,
  FlowTrigger,
  MagicFlowOption,
  NamespaceOption,
  NamespaceRecord,
  RecipeRecord,
} from './MagicFlowAdmin.types';

const OrganizationFilterBase = OrganizationFilter as any;

type FlowFormValues = {
  name: string;
  namespace_id: string;
  recipe_id?: string;
  default_priority?: string;
  assignment_strategy?: string;
  assignment_roles?: string[];
  due_date_days?: number;
  run_name?: string;
};

type ParsedFlowJson = {
  steps: FlowStep[];
  triggers: FlowTrigger[];
  custom_variables: Record<string, unknown>;
};

const buildInitialJson = (
  steps?: FlowStep[],
  triggers?: FlowTrigger[],
  customVariables?: Record<string, unknown>
) =>
  JSON.stringify(
    {
      steps: steps ?? [],
      triggers: triggers ?? [],
      custom_variables: customVariables ?? {},
    },
    null,
    2
  );

const DEFAULT_JSON = buildInitialJson(
  DEFAULT_FLOW_BUILDER_VALUE.steps,
  DEFAULT_FLOW_BUILDER_VALUE.triggers,
  DEFAULT_FLOW_BUILDER_VALUE.custom_variables
);

const parseFlowJson = (raw: string): ParsedFlowJson => {
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Top-level value must be an object');
  }
  return {
    steps: Array.isArray(parsed.steps) ? parsed.steps : [],
    triggers: Array.isArray(parsed.triggers) ? parsed.triggers : [],
    custom_variables:
      parsed.custom_variables && typeof parsed.custom_variables === 'object'
        ? parsed.custom_variables
        : {},
  };
};

const MagicFlowFlowEditorContent = () => {
  const dispatch = useDispatch<any>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId?: string }>();
  const isEditMode = Boolean(flowId && flowId !== 'new');

  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const hasOrgSelected = Boolean(orgUuid);
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const [form] = Form.useForm<FlowFormValues>();

  const [flowJson, setFlowJson] = useState(DEFAULT_JSON);
  const [jsonError, setJsonError] = useState<string | null>(null);
  // Watched so the right-rail prefill card stays in sync with the form's
  // recipe_id value (Form.useWatch returns the current value reactively).
  const watchedRecipeId = Form.useWatch('recipe_id', form);

  const [manualRunModalOpen, setManualRunModalOpen] = useState(false);
  const [manualRunInputsJson, setManualRunInputsJson] = useState('{}');
  const [manualRunPriority, setManualRunPriority] = useState<string>();

  const scopedHeaders = useMemo(() => {
    return {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
  }, [superAdminHeaders]);

  usePageHeader({
    title: isEditMode ? 'Edit Flow' : 'Create Flow',
  });

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  const { data: namespaceOptions = [] } = useQuery({
    queryKey: ['magicflow-namespaces', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{ namespaces?: NamespaceRecord[] }>('/namespace', {
          headers: scopedHeaders,
          params: {
            page: 1,
            page_size: 200,
            sort_by: 'updated_at',
            sort_direction: 'desc',
          },
        })
        .then(
          (res) =>
            (res.data?.namespaces || [])
              .filter((namespace) => namespace?.namespace_id && namespace?.name)
              .map((namespace) => ({
                value: String(namespace.namespace_id),
                label: String(namespace.name),
              })) as NamespaceOption[]
        ),
  });

  const { data: priorities = [] } = useQuery({
    queryKey: ['magicflow-flow-run-priorities', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{
          priorities?: FlowRunPriorityRecord[];
          flow_run_priorities?: FlowRunPriorityRecord[];
        }>('/flow_run_priority', {
          headers: scopedHeaders,
        })
        .then(
          (res) => res.data.priorities || res.data.flow_run_priorities || []
        ),
  });

  const priorityOptions: MagicFlowOption[] = useMemo(
    () =>
      priorities.map((priority) => ({
        value: priority.key,
        label: priority.name || priority.key,
      })),
    [priorities]
  );

  const { data: recipes = [] } = useQuery({
    queryKey: ['magicflow-recipes', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{ recipes: RecipeRecord[] }>('/recipe', {
          headers: scopedHeaders,
          params: { page: 1, page_size: 200, active_only: true },
        })
        .then((res) => res.data.recipes || []),
  });

  const recipeOptions: MagicFlowOption[] = useMemo(
    () =>
      recipes.map((recipe) => ({
        value: recipe.recipe_id,
        label: `${recipe.name} (v${recipe.version ?? '?'})`,
      })),
    [recipes]
  );

  const { data: existingFlow, isLoading: isLoadingFlow } = useQuery({
    queryKey: ['magicflow-flow-detail', orgUuid, flowId],
    enabled: hasOrgSelected && isEditMode,
    queryFn: () =>
      magicFlowApi
        .get<{ flow: FlowRecord }>(`/flow/${flowId}`, {
          headers: scopedHeaders,
        })
        .then((res) => res.data.flow),
  });

  const { data: flowVersions = [] } = useQuery({
    queryKey: [
      'magicflow-flow-versions',
      orgUuid,
      existingFlow?.key,
      existingFlow?.namespace_id,
    ],
    enabled:
      hasOrgSelected &&
      Boolean(existingFlow?.key) &&
      Boolean(existingFlow?.namespace_id),
    queryFn: () =>
      magicFlowApi
        .get<{ flows: FlowRecord[] }>('/flow', {
          headers: scopedHeaders,
          params: {
            key: existingFlow!.key,
            namespace_id: existingFlow!.namespace_id,
            latest_only: false,
            page: 1,
            page_size: 200,
          },
        })
        .then((res) => res.data?.flows || []),
  });

  useEffect(() => {
    if (isEditMode) {
      if (!existingFlow) return;
      form.setFieldsValue({
        name: existingFlow.name,
        namespace_id: existingFlow.namespace_id,
        recipe_id: existingFlow.recipe_id,
        default_priority: existingFlow.default_priority,
        assignment_strategy: existingFlow.assignment_strategy,
        assignment_roles: existingFlow.assignment_roles || [],
        due_date_days: existingFlow.due_date_days,
        run_name: existingFlow.run_name ?? undefined,
      });
      setFlowJson(
        buildInitialJson(
          existingFlow.steps,
          existingFlow.triggers,
          existingFlow.custom_variables
        )
      );
      setJsonError(null);
      return;
    }

    form.setFieldsValue({
      name: '',
      namespace_id: '',
      recipe_id: undefined,
      default_priority: undefined,
      assignment_strategy: undefined,
      assignment_roles: [],
      due_date_days: undefined,
      run_name: undefined,
    });
    setFlowJson(DEFAULT_JSON);
    setJsonError(null);
  }, [existingFlow, form, isEditMode]);

  const handleJsonChange = (raw: string) => {
    setFlowJson(raw);
    try {
      parseFlowJson(raw);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleRecipeChange = (recipeId?: string) => {
    if (!recipeId) {
      form.setFieldValue('recipe_id', undefined);
      return;
    }

    const recipe = recipes.find((item) => item.recipe_id === recipeId);
    if (!recipe) return;

    form.setFieldsValue({
      recipe_id: recipeId,
      namespace_id: recipe.namespace_id,
    });
    setFlowJson(
      buildInitialJson(recipe.steps, recipe.triggers, recipe.custom_variables)
    );
    setJsonError(null);
  };

  const saveFlowMutation = useMutation({
    mutationFn: async (payload: FlowFormValues & ParsedFlowJson) => {
      if (isEditMode) {
        const updatePayload: Record<string, unknown> = {
          name: payload.name,
          steps: payload.steps,
          triggers: payload.triggers,
          custom_variables: payload.custom_variables,
          assignment_roles: payload.assignment_roles,
        };
        if (payload.default_priority != null) {
          updatePayload.default_priority = payload.default_priority;
        }
        if (payload.assignment_strategy != null) {
          updatePayload.assignment_strategy = payload.assignment_strategy;
        }
        if (payload.due_date_days != null) {
          updatePayload.due_date_days = payload.due_date_days;
        }
        // Always forward run_name on edit so clearing the field clears the
        // template on the new version (an empty template disables per-run naming).
        updatePayload.run_name = payload.run_name ?? '';
        return magicFlowApi.put(`/flow/${flowId}`, updatePayload, {
          headers: scopedHeaders,
        });
      }

      const createPayload: Record<string, unknown> = {
        name: payload.name,
        namespace_id: payload.namespace_id,
        steps: payload.steps,
        triggers: payload.triggers,
        custom_variables: payload.custom_variables,
        assignment_roles: payload.assignment_roles,
      };
      if (payload.recipe_id != null) {
        createPayload.recipe_id = payload.recipe_id;
      }
      if (payload.default_priority != null) {
        createPayload.default_priority = payload.default_priority;
      }
      if (payload.assignment_strategy != null) {
        createPayload.assignment_strategy = payload.assignment_strategy;
      }
      if (payload.due_date_days != null) {
        createPayload.due_date_days = payload.due_date_days;
      }
      if (payload.run_name) {
        createPayload.run_name = payload.run_name;
      }
      return magicFlowApi.post('/flow', createPayload, {
        headers: scopedHeaders,
      });
    },
    onSuccess: () => {
      showToastSuccess(
        isEditMode
          ? 'Flow updated (new version created, inactive by default)'
          : 'Flow created (inactive by default)'
      );
      queryClient.invalidateQueries({ queryKey: ['magicflow-flows', orgUuid] });
      navigate('/superadmin/magicflow/flows');
    },
    onError: (error) => {
      showToastError(getApiErrorMessage(error as any) || 'Failed to save flow');
    },
  });

  const activateVersionMutation = useMutation({
    mutationFn: (versionFlowId: string) =>
      magicFlowApi.post(
        `/flow/${versionFlowId}/activate`,
        {},
        { headers: scopedHeaders }
      ),
    onSuccess: () => {
      showToastSuccess('Flow version activated');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-flow-detail', orgUuid, flowId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'magicflow-flow-versions',
          orgUuid,
          existingFlow?.key,
          existingFlow?.namespace_id,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['magicflow-flows', orgUuid] });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to activate flow version'
      );
    },
  });

  const runFlowMutation = useMutation({
    mutationFn: (payload: {
      inputs?: Record<string, unknown>;
      priority?: string;
    }) =>
      magicFlowApi.post(`/flow/${flowId}/run`, payload, {
        headers: scopedHeaders,
      }),
    onSuccess: () => {
      showToastSuccess('Manual flow run started');
      setManualRunModalOpen(false);
      setManualRunInputsJson('{}');
      setManualRunPriority(undefined);
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to start manual flow run'
      );
    },
  });

  const handleSave = async () => {
    if (!hasOrgSelected) {
      showToastError('Select an organization before saving the flow.');
      return;
    }

    let parsed: ParsedFlowJson;
    try {
      parsed = parseFlowJson(flowJson);
      setJsonError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON';
      setJsonError(message);
      showToastError(`Invalid JSON: ${message}`);
      return;
    }

    await form.validateFields();
    const values = form.getFieldsValue(true) as FlowFormValues;
    saveFlowMutation.mutate({
      name: values.name,
      namespace_id: values.namespace_id,
      recipe_id: values.recipe_id,
      default_priority: values.default_priority,
      assignment_strategy: values.assignment_strategy,
      assignment_roles: values.assignment_roles,
      due_date_days: values.due_date_days,
      // Empty string (not undefined) so an edit can clear an existing template;
      // the update path always forwards run_name, the create path drops it when blank.
      run_name: values.run_name?.trim() ?? '',
      steps: parsed.steps,
      triggers: parsed.triggers,
      custom_variables: parsed.custom_variables,
    });
  };

  const handleManualRun = () => {
    let parsedInputs: Record<string, unknown> = {};
    if (manualRunInputsJson.trim()) {
      try {
        const parsed = JSON.parse(manualRunInputsJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedInputs = parsed;
        } else {
          showToastError('Inputs must be a JSON object');
          return;
        }
      } catch (error) {
        showToastError('Inputs must be valid JSON');
        return;
      }
    }

    runFlowMutation.mutate({
      inputs: parsedInputs,
      priority: manualRunPriority,
    });
  };

  const versionsForCard = useMemo(
    () =>
      flowVersions.map((version) => ({
        id: version.flow_id,
        version: version.version,
        is_active: version.is_active,
        updated_at: version.updated_at,
      })),
    [flowVersions]
  );

  const topFormItemLayout = {
    labelCol: { flex: '160px' },
    wrapperCol: { flex: 'auto' },
  };

  return (
    <div className="page-wrapper">
      <div className="flex justify-between items-center gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BackIconMobile
            className=""
            onClick={() => navigate('/superadmin/magicflow/flows')}
          />
          <Typography.Text className="font-medium">Flows</Typography.Text>
          {isEditMode && existingFlow ? (
            <Tag color={existingFlow.is_active ? 'green' : 'default'}>
              v{existingFlow.version ?? '?'}
              {existingFlow.is_active ? ' • active' : ' • inactive'}
            </Tag>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-md text-gray-600 font-medium">
            Select organization:
          </label>
          <OrganizationFilterBase />
        </div>
      </div>

      {!hasOrgSelected && (
        <Alert
          className="mt-3"
          appearance="info"
          message="Select an organization to edit flows."
        />
      )}

      <div className="mt-3 grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-9">
          <Form form={form} layout="horizontal" labelAlign="left">
            <Card
              title="Flow Settings"
              className="mb-3"
              padding={{ body: 'sm', header: 'sm' }}
            >
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
                {...topFormItemLayout}
              >
                <Input placeholder="Flow name" />
              </Form.Item>

              <Form.Item
                label="Namespace"
                name="namespace_id"
                rules={[{ required: true, message: 'Namespace is required' }]}
                {...topFormItemLayout}
              >
                <Select
                  showSearch
                  allowClear
                  options={namespaceOptions}
                  placeholder="Select namespace"
                  optionFilterProp="label"
                  disabled={isEditMode}
                />
              </Form.Item>

              <Form.Item
                label="Default Priority"
                name="default_priority"
                {...topFormItemLayout}
              >
                <Select
                  showSearch
                  allowClear
                  options={priorityOptions}
                  placeholder="Select default priority"
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Assignment Strategy"
                name="assignment_strategy"
                {...topFormItemLayout}
              >
                <Select
                  showSearch
                  allowClear
                  options={[
                    { value: 'round_robin', label: 'Round Robin' },
                    { value: 'random', label: 'Random' },
                  ]}
                  placeholder="Select assignment strategy"
                />
              </Form.Item>

              <Form.Item
                label="Assignment Roles"
                name="assignment_roles"
                {...topFormItemLayout}
              >
                <Select
                  mode="tags"
                  tokenSeparators={[',']}
                  placeholder="Enter Frontegg role names"
                />
              </Form.Item>

              <Form.Item
                label="Due Date (days)"
                name="due_date_days"
                {...topFormItemLayout}
              >
                <InputNumber min={0} precision={0} className="w-full" />
              </Form.Item>

              <Form.Item
                label="Run Name"
                name="run_name"
                tooltip="Template for naming each run, resolved from the run's inputs at creation. Use $.field for trigger inputs and $vars.key for custom variables, e.g. $.patient_name ($.patient_phone_number)."
                extra="Resolved against trigger inputs at creation. Leave blank to fall back to the flow name."
                {...topFormItemLayout}
              >
                <Input placeholder="$.patient_name ($.patient_phone_number)" />
              </Form.Item>
            </Card>

            <FlowBuilder
              value={flowJson}
              onChange={handleJsonChange}
              error={jsonError}
            />
          </Form>
        </div>

        <div className="xl:col-span-3">
          {isEditMode ? (
            <MagicFlowVersionsProvider
              versions={versionsForCard}
              activatingId={activateVersionMutation.variables}
              isActivatingPending={activateVersionMutation.isPending}
              onActivate={(id) => activateVersionMutation.mutate(id)}
              onSelect={(id) => navigate(`/superadmin/magicflow/flows/${id}`)}
              currentId={flowId}
            >
              <MagicFlowVersionsCard />
            </MagicFlowVersionsProvider>
          ) : (
            <MagicFlowRecipePrefillCard
              value={watchedRecipeId}
              options={recipeOptions}
              onChange={handleRecipeChange}
              disabled={!hasOrgSelected}
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-between gap-2">
        <Space>
          {isEditMode && existingFlow?.is_active ? (
            <Button
              variant="outlined"
              appearance="secondary"
              icon={<PlayCircleOutlined />}
              onClick={() => setManualRunModalOpen(true)}
            >
              Run now
            </Button>
          ) : null}
        </Space>
        <Space>
          <Button
            variant="outlined"
            appearance="secondary"
            onClick={() => navigate('/superadmin/magicflow/flows')}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            loading={saveFlowMutation.isPending}
            onClick={handleSave}
            disabled={!hasOrgSelected || isLoadingFlow}
          >
            {isEditMode ? 'Save (new version)' : 'Create Flow'}
          </Button>
        </Space>
      </div>

      <Modal
        open={manualRunModalOpen}
        onClose={() => setManualRunModalOpen(false)}
        title="Run flow manually"
        primaryAction={{
          label: 'Run',
          onClick: handleManualRun,
          loading: runFlowMutation.isPending,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setManualRunModalOpen(false),
        }}
      >
        <div className="space-y-3">
          <div>
            <Typography.Text className="block mb-1">Priority</Typography.Text>
            <Select
              className="w-full"
              showSearch
              allowClear
              options={priorityOptions}
              value={manualRunPriority}
              onChange={(value) => setManualRunPriority(value)}
              placeholder="Default flow priority"
              optionFilterProp="label"
            />
          </div>
          <div>
            <Typography.Text className="block mb-1">
              Inputs (JSON)
            </Typography.Text>
            <Input.TextArea
              className="font-mono"
              autoSize={{ minRows: 6, maxRows: 12 }}
              value={manualRunInputsJson}
              onChange={(event) => setManualRunInputsJson(event.target.value)}
              placeholder='{"patient_id":"..."}'
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const MagicFlowFlowEditorPage = () => (
  <SuperAdminScope>
    <MagicFlowFlowEditorContent />
  </SuperAdminScope>
);

export default MagicFlowFlowEditorPage;
